/**
 * useBookingLinks — Fetch reservation links for BookingDetailModal (Documents & Links)
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-2d: Extracted from BookingDetailModal.jsx links state and effect.
 *
 * Purpose:
 *   Fetch reservation links when modal is open and links feature is enabled.
 *   Same endpoint, params, gating, and loading/error/empty behavior as the original inline effect.
 *
 * Workflow:
 *   1. When !linksEnabled: clear state and return.
 *   2. When isOpen && reservationId && linksEnabled: fetch only if !linksLoading && links.length === 0 && !linksError.
 *      Call getReservationLinksFn({ reservation_id, include_property_links: true }); 8s timeout.
 *      Success (data.outcome === 'success'): set links = data.links || data.data?.links || [].
 *      404 → empty, no error; 401/403/500 → error string with HTTP status; other → err.message or generic.
 *   3. When !isOpen: clear state.
 *
 * Assumptions & Invariants:
 *   - getReservationLinksFn({ reservation_id, include_property_links }) matches ownerApi.getReservationLinks.
 *   - linksEnabled is OWNER_FEATURES.links (passed from parent); hook does not import OWNER_FEATURES.
 *   - Effect deps preserved: [isOpen, reservationId, linksEnabled, links.length, linksLoading, linksError].
 *   - No new endpoints; no change to call count or timing.
 *
 * Dependencies:
 *   - React (useState, useEffect)
 *   - getReservationLinksFn passed in by caller
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-2d: Extracted from BookingDetailModal.jsx; AbortController + isActive added.
 */

import { useState, useEffect } from 'react';

/**
 * @param {Object} opts
 * @param {boolean} opts.isOpen - Modal open
 * @param {string|number|null|undefined} opts.reservationId - Reservation ID
 * @param {string|number|null|undefined} opts.listingId - Hostaway listing ID (required for full list; from booking.listing_id)
 * @param {boolean} opts.linksEnabled - Feature gate (e.g. OWNER_FEATURES.links)
 * @param {function(Object): Promise<Object>} opts.getReservationLinksFn - e.g. getReservationLinks
 * @returns {{ linksLoading: boolean, linksError: string|null, links: Array }}
 */
export function useBookingLinks({
  isOpen,
  reservationId,
  listingId,
  linksEnabled,
  getReservationLinksFn,
}) {
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;

    if (!linksEnabled) {
      if (links.length > 0 || linksLoading || linksError) {
        setLinks([]);
        setLinksLoading(false);
        setLinksError(null);
      }
      return () => {
        abortController.abort();
        isActive = false;
      };
    }

    if (isOpen && reservationId && listingId) {
      if (!linksLoading && links.length === 0 && !linksError) {
        setLinksLoading(true);
        setLinksError(null);

        const timeoutId = setTimeout(() => {
          if (!isActive) return;
          console.warn('[useBookingLinks] Links fetch timeout after 8s');
          setLinksLoading(false);
          setLinksError('Could not load links (timeout)');
          setLinks([]);
        }, 8000);

        getReservationLinksFn({ reservation_id: reservationId, listing_id: listingId, include_property_links: true })
          .then((data) => {
            clearTimeout(timeoutId);
            if (!isActive) return;
            if (data.outcome === 'success') {
              const linksArray = data.links || data.data?.links || [];
              setLinks(linksArray);
              setLinksError(null);
            } else {
              setLinks([]);
              setLinksError(null);
            }
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            if (!isActive) return;
            if (err?.name === 'AbortError') return;
            console.error('[useBookingLinks] Error fetching links:', err);
            if (err.status === 404) {
              setLinks([]);
              setLinksError(null);
            } else if (err.status === 401 || err.status === 403 || err.status === 500) {
              setLinks([]);
              setLinksError(`Could not load links (HTTP ${err.status})`);
            } else {
              setLinks([]);
              setLinksError(err.message || `Could not load links (HTTP ${err.status || 'unknown'})`);
            }
          })
          .finally(() => {
            clearTimeout(timeoutId);
            if (isActive) setLinksLoading(false);
          });

        return () => {
          clearTimeout(timeoutId);
          abortController.abort();
          isActive = false;
        };
      }
    } else if (!isOpen) {
      if (linksLoading || links.length > 0 || linksError) {
        setLinks([]);
        setLinksLoading(false);
        setLinksError(null);
      }
    }

    return () => {
      abortController.abort();
      isActive = false;
    };
  }, [isOpen, reservationId, listingId, linksEnabled, links.length, linksLoading, linksError]);

  return { linksLoading, linksError, links };
}
